<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import TasksAPI from 'dashboard/api/tasksApi';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { getTaskDueDate } from 'dashboard/helper/taskRecurrence';

const props = defineProps({
  contactId: { type: Number, default: null },
  companyId: { type: Number, default: null },
  dealId: { type: Number, default: null },
});

const emit = defineEmits(['loaded']);

const { t } = useI18n();

const tasks = ref([]);
const isLoading = ref(false);

const sortedTasks = computed(() =>
  [...tasks.value].sort((a, b) => {
    const aDone = a.status === 'completed';
    const bDone = b.status === 'completed';
    if (aDone !== bDone) return aDone ? 1 : -1;
    return a.dueDate - b.dueDate;
  })
);

const formatDueDate = task =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(getTaskDueDate(task));

const fetchTasks = async () => {
  isLoading.value = true;
  try {
    const {
      data: { payload },
    } = await TasksAPI.get({
      contact_id: props.contactId,
      company_id: props.companyId,
      deal_id: props.dealId,
    });
    tasks.value = camelcaseKeys(payload, { deep: true });
  } catch {
    tasks.value = [];
  } finally {
    isLoading.value = false;
    emit(
      'loaded',
      tasks.value.filter(task => task.status === 'pending').length
    );
  }
};

watch(() => [props.contactId, props.companyId, props.dealId], fetchTasks);
onMounted(fetchTasks);

defineExpose({ fetchTasks });
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="isLoading" class="flex justify-center py-6">
      <Spinner />
    </div>
    <p
      v-else-if="!sortedTasks.length"
      class="py-6 mb-0 text-sm text-center text-n-slate-11"
    >
      {{ t('TASKS.LINKED.EMPTY') }}
    </p>
    <template v-else>
      <div
        v-for="task in sortedTasks"
        :key="`${task.id}-${task.occurrenceDate}`"
        class="flex items-start gap-2 p-3 rounded-xl bg-n-alpha-black2"
      >
        <Icon
          :icon="
            task.status === 'completed'
              ? 'i-lucide-circle-check'
              : 'i-lucide-circle'
          "
          class="flex-shrink-0 mt-0.5 size-4"
          :class="
            task.status === 'completed' ? 'text-n-teal-9' : 'text-n-slate-10'
          "
        />
        <div class="flex flex-col min-w-0 gap-0.5">
          <span
            class="text-sm font-medium truncate text-n-slate-12"
            :class="{
              'line-through text-n-slate-10': task.status === 'completed',
            }"
          >
            {{ task.title }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ formatDueDate(task) }}
            <template v-if="task.assignedTo">
              · {{ task.assignedTo.name }}
            </template>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>
